<template>
  <v-container fluid class="os">

    <div class="os-head">
      <div class="os-firma">
        <div class="os-firma-ime">{{ comp.CompanyNameShort }}</div>
        <div>{{ comp.CompanyStreet }} {{ comp.CompanyBuildingNameNumber }}</div>
        <div>OIB: {{ comp.CompanyVATID }}</div>
      </div>
      <div class="os-naslov">
        <h2>Skupno knjiženje računa</h2>
        <div class="os-meta">poslovna godina {{ godina }}<span class="os-sep">·</span>knjiženje prema ranijim knjiženjima partnera</div>
      </div>
      <div class="os-akcije">
        <v-btn small outlined :loading="!ucitano" @click="ucitaj">Osvježi</v-btn>
        <v-btn small text color="primary" @click="natrag">← Računi</v-btn>
      </div>
    </div>

    <div v-if="greska" class="ak-greska" role="alert">{{ greska }}</div>

    <v-progress-linear v-if="!ucitano && !greska" indeterminate color="primary" class="mt-2"></v-progress-linear>

    <template v-if="ucitano">
      <div class="os-sazetak">
        <div class="os-kut">
          <span>Neproknjiženih računa</span>
          <b>{{ dokumenti.length }}</b>
          <small>{{ prikaz.length }} u prikazu</small>
        </div>
        <div class="os-kut os-istaknuto">
          <span>Spremno za knjiženje</span>
          <b>{{ zbroj.sigurnih }}</b>
          <small>{{ formatPrice(zbroj.iznosSigurnih) }} {{ currency }}</small>
        </div>
        <div class="os-kut">
          <span>Traži pregled</span>
          <b>{{ dokumenti.length - zbroj.sigurnih }}</b>
          <small>knjiže se ručno ili uz izmjenu</small>
        </div>
        <div class="os-kut">
          <span>Označeno</span>
          <b>{{ zbroj.odabranih }}</b>
          <small>{{ formatPrice(zbroj.iznosOdabranih) }} {{ currency }}</small>
        </div>
      </div>

      <div class="os-alati">
        <span class="os-oznaka">Vrsta</span>
        <div class="os-seg" role="group" aria-label="Vrsta računa">
          <button type="button" :aria-pressed="String(vrsta === 'sve')" @click="vrsta = 'sve'">Sve</button>
          <button type="button" :aria-pressed="String(vrsta === 1)" @click="vrsta = 1">Ulazni (URA)</button>
          <button type="button" :aria-pressed="String(vrsta === 2)" @click="vrsta = 2">Izlazni (IRA)</button>
        </div>
        <div class="os-seg" role="group" aria-label="Označavanje">
          <button type="button" @click="oznaci('sigurne')">Označi sigurne</button>
          <button type="button" @click="oznaci('nista')">Odznači sve</button>
        </div>
        <input v-model="trazi" class="os-trazi" type="search" placeholder="partner, broj ili račun" aria-label="Traži račun">
      </div>

      <div v-if="prikaz.length === 0" class="os-prazno">
        {{ dokumenti.length === 0 ? 'Svi računi poslovne godine su proknjiženi.' : 'Nijedan račun ne odgovara odabranim uvjetima.' }}
      </div>

      <div v-else class="os-scroll">
        <table class="os-tbl ak-tbl">
          <thead>
            <tr>
              <th class="l"><span class="ak-sr">Odabir</span></th>
              <th class="l">Račun</th>
              <th class="l">Datum</th>
              <th class="l">Partner</th>
              <th>Iznos</th>
              <th class="l">Prijedlog knjiženja</th>
              <th class="l">Stanje</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="d in prikaz" :key="d.id" :class="{'ak-sigurno': d.sigurno}">
              <td>
                <input
                  type="checkbox"
                  :checked="!!odabrani[d.id]"
                  :disabled="!d.uredu"
                  :aria-label="'Označi ' + d.knjiga + ' ' + d.broj"
                  @change="prebaci(d)"
                >
              </td>
              <td class="doc">{{ d.knjiga }} {{ d.broj }}<small v-if="d.InvoiceNumber" class="ak-rn"> rn: {{ d.InvoiceNumber }}</small></td>
              <td class="doc">{{ datum(d.datum) }}</td>
              <td class="opis"><b>{{ d.partnerName }}</b><small>{{ d.partnerVATID }}</small></td>
              <td class="num">{{ formatPrice(d.iznos) }}</td>
              <td class="opis">
                <template v-if="d.stavke.length">
                  <span v-for="(s, i) in d.stavke" :key="i" class="ak-stavka">
                    <b>{{ s.account4 }}</b>
                    <span class="ak-strana">{{ s.Debit ? 'D' : 'P' }}</span>
                    {{ formatPrice(s.Debit || s.Credit) }}
                    <em v-if="s.naziv">{{ s.naziv }}</em>
                  </span>
                </template>
                <span v-else class="ak-nema">nema prijedloga</span>
              </td>
              <td class="opis">
                <span v-if="d.sigurno" class="ak-cip ak-ok">sigurno · {{ d.slaganje }} od {{ d.odN }}</span>
                <span v-else class="ak-cip ak-upit">{{ (d.razlozi && d.razlozi.length) ? d.razlozi[0] : 'traži pregled' }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="ak-podnozje">
        <v-btn color="primary" :disabled="zbroj.odabranih === 0 || radi" :loading="radi" @click="proknjizi">
          Proknjiži označene ({{ zbroj.odabranih }})
        </v-btn>
        <span class="ak-biljeska">
          Knjiži se samo ono što zatvara iznos računa. Sve proknjiženo može se otvoriti i ispraviti kao i ručno knjiženje.
        </span>
      </div>

      <div v-if="rezultat" class="ak-rezultat">
        <div class="ak-rezultat-naslov">Proknjiženo {{ rezultat.proknjizeno.length }}, preskočeno {{ rezultat.preskoceno.length }}</div>
        <ul v-if="rezultat.preskoceno.length" class="ak-popis">
          <li v-for="p in rezultat.preskoceno" :key="'p' + p.id"><b>{{ p.dokument }}</b> — {{ p.razlog }}</li>
        </ul>
      </div>
    </template>

    <v-snackbar v-model="snackbar" :timeout="timeout" bottom :color="color">{{ text }}</v-snackbar>
  </v-container>
</template>

<script>
// Skupno knjizenje ulaznih i izlaznih racuna.
//
// Server za svaki neproknjizen racun predlaze knjizenje prema zadnjim
// knjizenjima istog partnera u istoj knjizi i oznaci je li dovoljno sigurno
// (zadnja cetiri knjizenja ista i prijedlog zatvara iznos racuna). Sigurni su
// oznaceni po dolasku, ostali se mogu oznaciti rucno.

import dayjs from 'dayjs'
import MainLedgerKnjizenjeService from '@/services/MainLedgerKnjizenjeService'

const poruka = (err) => {
  if (err && err.response && err.response.data && err.response.data.error) { return err.response.data.error }
  return err && err.message ? err.message : String(err)
}

export default {
  name: 'MainLedgerAutoKnjizenje',
  data () {
    return {
      comp: {},
      currency: 'EUR',
      ucitano: false,
      greska: '',
      dokumenti: [],
      odabrani: {},
      vrsta: 'sve',
      trazi: '',
      radi: false,
      rezultat: null,
      snackbar: false,
      text: '',
      color: 'green',
      timeout: 4000
    }
  },
  computed: {
    godina () {
      return parseInt(this.$store.state.businessYear) || dayjs().year()
    },
    prikaz () {
      const t = this.trazi.trim().toLowerCase()
      return this.dokumenti.filter(d => {
        if (this.vrsta !== 'sve' && parseInt(d.MLType) !== this.vrsta) { return false }
        if (!t) { return true }
        return (d.partnerName + ' ' + d.partnerVATID + ' ' + d.knjiga + ' ' + d.broj + ' ' + (d.InvoiceNumber || ''))
          .toLowerCase().indexOf(t) !== -1
      })
    },
    zbroj () {
      const z = {sigurnih: 0, iznosSigurnih: 0, odabranih: 0, iznosOdabranih: 0}
      this.dokumenti.forEach(d => {
        if (d.sigurno) { z.sigurnih += 1; z.iznosSigurnih += d.iznos }
        if (this.odabrani[d.id]) { z.odabranih += 1; z.iznosOdabranih += d.iznos }
      })
      return z
    }
  },
  async mounted () {
    this.comp = this.$store.state.companyActiv ? this.$store.state.companyActiv : {}
    this.currency = this.godina >= 2023 ? 'EUR' : 'Kn'
    await this.ucitaj()
  },
  methods: {
    formatPrice (value) {
      const n = parseFloat(value)
      return (isNaN(n) ? 0 : n).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    },
    datum (d) {
      return d ? dayjs(d).format('DD.MM.YYYY') : ''
    },
    natrag () {
      this.$router.push({name: 'mainledger'})
    },
    async ucitaj () {
      this.ucitano = false
      this.greska = ''
      this.rezultat = null
      try {
        const {data} = await MainLedgerKnjizenjeService.neproknjizeni({
          CompanyId: this.$store.state.companyid,
          BusinessYear: this.godina
        })
        this.dokumenti = data && data.dokumenti ? data.dokumenti : []
        // Sigurni su odmah oznaceni - to je posao koji se moze obaviti u jednom potezu.
        const novi = {}
        this.dokumenti.forEach(d => { if (d.sigurno) { novi[d.id] = true } })
        this.odabrani = novi
      } catch (err) {
        this.greska = 'Popis nije učitan: ' + poruka(err)
      }
      this.ucitano = true
    },
    prebaci (d) {
      this.$set(this.odabrani, d.id, !this.odabrani[d.id])
    },
    oznaci (sto) {
      const novi = {}
      if (sto === 'sigurne') {
        this.prikaz.forEach(d => { if (d.sigurno) { novi[d.id] = true } })
      }
      this.odabrani = novi
    },
    async proknjizi () {
      const ids = this.dokumenti.filter(d => this.odabrani[d.id]).map(d => d.id)
      if (ids.length === 0) { return }
      const sveSigurno = this.dokumenti.filter(d => this.odabrani[d.id]).every(d => d.sigurno)
      if (!sveSigurno && !window.confirm('Među označenima ima računa koje prijedlog nije označio sigurnima. Proknjižiti ih svejedno?')) {
        return
      }
      this.radi = true
      try {
        const {data} = await MainLedgerKnjizenjeService.proknjizi({
          CompanyId: this.$store.state.companyid,
          ids: ids,
          samoSigurne: false,
          UserId: this.$store.state.user ? this.$store.state.user.id : null,
          email: this.$store.state.user ? this.$store.state.user.email : '',
          First: this.$store.state.user ? this.$store.state.user.First : '',
          Last: this.$store.state.user ? this.$store.state.user.Last : ''
        })
        this.rezultat = {proknjizeno: data.proknjizeno || [], preskoceno: data.preskoceno || []}
        this.text = 'Proknjiženo ' + this.rezultat.proknjizeno.length + ', preskočeno ' + this.rezultat.preskoceno.length + '.'
        this.color = this.rezultat.proknjizeno.length > 0 ? 'green' : 'orange darken-3'
        this.snackbar = true
        const rez = this.rezultat
        await this.ucitaj()
        this.rezultat = rez
      } catch (err) {
        this.text = 'Knjiženje nije izvršeno: ' + poruka(err)
        this.color = 'red'
        this.snackbar = true
      }
      this.radi = false
    }
  }
}
</script>

<style scoped src="./otvoreneStavke.css"></style>

<style scoped>
.ak-tbl td { vertical-align: top; padding-top: 6px; padding-bottom: 6px; }
.ak-tbl td.opis small { display: block; color: #79827e; font-size: 12px; }
.ak-tbl td.opis b { font-weight: 600; }
.ak-sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); }
.ak-rn { color: #79827e; font-size: 12px; }

.ak-sigurno td { background: #f3f8f5; }
.os-tbl tbody tr.ak-sigurno:hover td { background: #eaf3ee; }

.ak-stavka { display: block; font-size: 13px; white-space: nowrap; }
.ak-stavka b { font-weight: 600; }
.ak-stavka em { font-style: normal; color: #79827e; padding-left: 6px; }
.ak-strana { display: inline-block; width: 14px; color: #79827e; font-weight: 600; font-size: 12px; }
.ak-nema { color: #79827e; font-size: 13px; }

.ak-cip {
  display: inline-block;
  padding: 1px 6px;
  border: 1px solid #c7cfcb;
  border-radius: 2px;
  font-size: 12px;
  white-space: normal;
}
.ak-ok { border-color: #9dc3ad; background: #eaf3ee; color: #1f6b43; font-weight: 600; white-space: nowrap; }
.ak-upit { border-color: #e7c99a; background: #fbf3e6; color: #8c5a1e; }

.ak-podnozje { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; padding: 12px 4px; border-top: 1px solid #c7cfcb; }
.ak-biljeska { color: #79827e; font-size: 13px; max-width: 70ch; }

.ak-rezultat { padding: 10px 4px 0; }
.ak-rezultat-naslov { font-weight: 600; }
.ak-popis { margin: 6px 0 0; padding-left: 18px; color: #4a524f; font-size: 13px; }

.ak-greska {
  margin: 8px 0;
  padding: 8px 12px;
  border: 1px solid #e0b4a8;
  border-radius: 2px;
  background: #fbeeea;
  color: #8c2f1e;
}
</style>
